// MIDAS V3 — Variant E status/conditions table chrome (MIDAS-ANSWER-FORMAT-1
// round 5b; Paper "MIDAS answer format — before / after", board S10, frame
// "Variant E" → "E answer" → "E table" + "E footer").
//
// Rendered ONLY for tables statusTableGrammar.ts annotated as the round-5
// status table; every other markdown table keeps markdownComponentsV3's plain
// renderers. Pieces:
//   StatusTableFrameV3  rounded frame + header bar (title · output · counts)
//                       + the optional <verified-line/> band under the bar
//                       (MIDAS-VERIFIED-LINE-1, VerifiedLineV3)
//                       + horizontal-scroll body (phone-compact class)
//   StatusCellV3        one body cell, styled by column role; the mark cell
//                       draws the round badge, an unverified row's ("?"
//                       without a pulse, r5c) symbol cell gets the
//                       "neoverené" label, the pulse cell keeps the r5
//                       <live-row/> lane (28 px) untouched; r6: a condition
//                       cell with a <cond-info/> explanation wraps its text in
//                       the CondInfoV3 trigger (styles in ./cond-info.css)
//   StatusMarkBadgeV3   22 px round ✕ / ✓ / ? / – badge, named for AT
//
// Paper values (get_jsx / get_computed_styles) → V3 tokens, so all four
// palettes (p-claude / p-machineid / p-teal / p-slate) follow:
//   frame     1px #E3DFD5 → --border, radius 8, #FFFFFF → --card
//   bar       #F4F1EA → --card-2, padding 10×14; title 11/14 700 0.1em caps
//             #8C8983 → --faint; output mono 13/16 --faint; counts 12/16 700
//             #D64545 → --red (--faint when nothing is missing)
//   rows      border-top 1px #EEEBE4 → --border-soft, padding 9×14, gap 12;
//             ✕ row #FFF5F5 → 5 % --red over --card
//   badge     22×22 radius 11, glyph 13/16 700; ✕ #D64545 → --red, ✓ #2E9E5B
//             → --green, ? #E3DFD5 → --border with #8C8983 → --faint glyph;
//             white glyph #FFFFFF → --card
//   cells     condition 15/18 --ink · address mono 13/16 --ink (110 px) ·
//             symbol mono 12/16 --faint (172 px) · pulse lane 28 px;
//             "?" row: everything --faint, address/symbol mono 11/14.
//             Address/symbol widths are floors: the identifier never wraps
//             (.midas-st__token) and widens its column instead.
// Inter → var(--font-sans), JetBrains Mono → var(--font-mono). Classes live in
// ./status-table.css. No MUI, no @emotion, no `sx`, no `dt` tokens.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { LiveRowEmptySlotV3 } from "./LiveRowV3";
import { CondInfoV3 } from "./CondInfoV3";
import { VerifiedLineV3 } from "./VerifiedLineV3";
import { isPlcMessagesCardTitle } from "./plcMessagesCardGrammar";
import {
  STATUS_MARK_GLYPH,
  type StatusCellInfo,
  type StatusMark,
  type StatusTableInfo,
} from "./statusTableGrammar";

type RenderText = (children: React.ReactNode) => React.ReactNode;

// en defaults (the shipped en/sk/cs strings live in chatBot.json → statusTable)
const MARK_LABEL_DEFAULT: Record<StatusMark, string> = {
  fail: "Not met",
  ok: "Met",
  unknown: "Unverified",
  na: "Not judged",
};
// r5c: a "?" row WITH a live pulse was read but cannot be judged — it is not
// "unverified", so its badge must not be announced as such.
const UNKNOWN_LIVE_LABEL_DEFAULT = "Read, cannot be judged";

/** Round mark badge. The glyph is decorative; the accessible name says it. */
export const StatusMarkBadgeV3: React.FC<{ mark: StatusMark; live?: boolean }> = ({
  mark,
  live = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const readNotJudged = mark === "unknown" && live;
  const label = (
    readNotJudged
      ? translate("statusTable.mark.unknownLive", {
          defaultValue: UNKNOWN_LIVE_LABEL_DEFAULT,
        })
      : translate(`statusTable.mark.${mark}`, {
          defaultValue: MARK_LABEL_DEFAULT[mark],
        })
  ) as string;
  return (
    <span
      className={`midas-st__mark midas-st__mark--${mark}`}
      role="img"
      aria-label={label}
      title={label}
      data-status-mark={readNotJudged ? "unknown-live" : mark}
    >
      <span aria-hidden="true">{STATUS_MARK_GLYPH[mark]}</span>
    </span>
  );
};

/** "4 chýbajú · 2 neoverené" — counted from the rows, nothing invented. The
 *  unverified count is "?" rows without a pulse (= the bot's N); "?" rows with
 *  a pulse and "–" rows are in neither count.
 *  PLC-MESSAGES-CARD-COUNTER-1: on the PLC messages card (told apart by its
 *  title, plcMessagesCardGrammar) the same ✕ count reads "N active". */
const StatusSummaryV3: React.FC<{ info: StatusTableInfo }> = ({ info }) => {
  const { translate } = useTranslation("chatBot");
  const fail = info.counts.fail;
  const parts: string[] = [];
  if (fail > 0) {
    parts.push(
      (isPlcMessagesCardTitle(info.title)
        ? translate("statusTable.summary.active", {
            count: fail,
            defaultValue: "{{count}} active",
          })
        : translate("statusTable.summary.fail", {
            count: fail,
            defaultValue: "{{count}} missing",
          })) as string
    );
  }
  if (info.unverified > 0) {
    parts.push(
      translate("statusTable.summary.unknown", {
        count: info.unverified,
        defaultValue: "{{count}} unverified",
      }) as string
    );
  }
  if (parts.length === 0) return null;
  return (
    <span
      className={
        fail > 0 ? "midas-st__summary midas-st__summary--fail" : "midas-st__summary"
      }
      data-status-summary=""
    >
      {parts.join(" · ")}
    </span>
  );
};

export const hasStatusSummary = (info: StatusTableInfo): boolean =>
  info.counts.fail > 0 || info.unverified > 0;

export const StatusTableFrameV3: React.FC<{
  info: StatusTableInfo;
  renderText: RenderText;
  /** MIDAS-VERIFIED-LINE-1: the verified-line sentence (marker already
   *  stripped by the caller); null / absent = no band. */
  verifiedLine?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ info, renderText, verifiedLine = null, children }) => {
  const { isPhone } = useBreakpoint();
  const hasBar = !!info.title || !!info.output || hasStatusSummary(info);
  const className = [
    "midas-st",
    isPhone ? "midas-st--phone" : "",
    hasBar ? "" : "midas-st--no-bar",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className} data-status-table="variant-e">
      {hasBar && (
        <div className="midas-st__bar" data-status-bar="">
          <span className="midas-st__titles">
            {info.title && (
              <span className="midas-st__title">{renderText(info.title)}</span>
            )}
            {info.output && (
              <span className="midas-st__output">{renderText(info.output)}</span>
            )}
          </span>
          <StatusSummaryV3 info={info} />
        </div>
      )}
      {/* MIDAS-VERIFIED-LINE-1 (owner 2026-10-11 "1b", Paper "Verified header
          line"): the band sits INSIDE the card, directly under the header bar
          and above the first row — full-bleed, outside the sideways scroll so
          the sentence wraps to the card width (verified-line.css). */}
      {verifiedLine !== null && verifiedLine !== undefined && (
        <VerifiedLineV3 renderText={renderText}>{verifiedLine}</VerifiedLineV3>
      )}
      {/* Same overflow pattern as every V3 table: on a narrow column (phone,
          or desktop with the dock open) the table keeps a usable min width and
          scrolls sideways inside the frame; the bar stays put. */}
      <div className="midas-st__scroll">
        <table className="midas-st__table">{children}</table>
      </div>
    </div>
  );
};

/** One body cell of a status table, by column role. */
export const StatusCellV3: React.FC<{
  cell: StatusCellInfo;
  isEmptyLiveSlot: boolean;
  renderText: RenderText;
  children?: React.ReactNode;
}> = ({ cell, isEmptyLiveSlot, renderText, children }) => {
  const { translate } = useTranslation("chatBot");
  const className = `midas-st__cell midas-st__cell--${cell.role}`;
  // MIDAS-STATUS-TABLE-NOWRAP-1: address + symbol hold PLC identifiers
  // (`DB10.DBX108.6`, `DB31."Axis1".E_PulseEnable`, `I 7.7`) that must never
  // break inside the token — they sit in a no-wrap span (status-table.css);
  // the condition prose wraps as before.
  const isToken = cell.role === "address" || cell.role === "symbol";
  const content = (kids: React.ReactNode) =>
    isToken ? (
      <span className="midas-st__token">{renderText(kids)}</span>
    ) : (
      renderText(kids)
    );

  if (cell.role === "mark") {
    return (
      <td className={className}>
        {cell.mark ? <StatusMarkBadgeV3 mark={cell.mark} live={cell.live} /> : null}
      </td>
    );
  }
  if (cell.role === "pulse" && isEmptyLiveSlot) {
    // r5 lane, unchanged: unverified row in a table that has a live lane.
    return (
      <td className={className}>
        <LiveRowEmptySlotV3 />
      </td>
    );
  }
  if (cell.role === "condition" && cell.condInfo) {
    // r6 <cond-info/>: the condition text becomes the ONE interactive
    // trigger of the cell (dotted underline + "i"); the explanation opens as
    // a hover tooltip (desktop) / tap popover (phone + tablet). The caller
    // (markdownComponentsV3 MarkdownTdV3) already stripped the sentinel.
    return (
      <td className={`${className} midas-st__cell--info`}>
        <CondInfoV3 text={cell.condInfo}>{content(children)}</CondInfoV3>
      </td>
    );
  }
  if (cell.unverifiedSuffix !== "none") {
    const label = translate("statusTable.unverified", {
      defaultValue: "unverified",
    }) as string;
    return (
      <td className={className}>
        {cell.unverifiedSuffix === "after" && content(children)}
        <span className="midas-st__unverified" data-status-unverified="">
          {cell.unverifiedSuffix === "after" ? ` · ${label}` : label}
        </span>
      </td>
    );
  }
  return <td className={className}>{content(children)}</td>;
};
